const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

interface AdminFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  queryParams?: Record<string, string>;
}

interface AdminFetchResult<T = unknown> {
  data: T | null;
  error: { message: string; status?: number } | null;
}

/**
 * Utility function for authenticated admin API calls.
 * Uses `credentials: 'include'` to send HttpOnly cookies.
 */
export async function adminFetch<T = unknown>(
  functionName: string,
  options: AdminFetchOptions = {}
): Promise<AdminFetchResult<T>> {
  const { method = 'GET', body, queryParams } = options;

  // Fallback when browsers block cross-site cookies: send the opaque session token via Authorization.
  // This is still validated server-side (no client-side admin checks).
  const sessionToken =
    typeof window !== 'undefined' ? sessionStorage.getItem('adminSessionToken') : null;

  let url = `${SUPABASE_URL}/functions/v1/${functionName}`;
  if (queryParams) {
    const params = new URLSearchParams(queryParams);
    url += `?${params.toString()}`;
  }

  try {
    const response = await fetch(url, {
      method,
      credentials: 'include', // Important: Sends HttpOnly cookies
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_KEY,
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        data: null,
        error: { message: data.error || 'Request failed', status: response.status }
      };
    }

    return { data: data as T, error: null };
  } catch (err) {
    return {
      data: null,
      error: { message: err instanceof Error ? err.message : 'Network error' }
    };
  }
}
